import { FC } from 'react';
import type { WidgetStyle } from './WidgetStylePicker';
import hcLogo from '../../../../assets/images/logo/logo-green-xl.png';

interface WidgetPreviewProps {
  style: WidgetStyle;
  primaryColor: string;
  providerName: string;
  squareLogoUrl?: string | null;
}

const WidgetPreview: FC<WidgetPreviewProps> = ({
  style,
  primaryColor,
  providerName,
  squareLogoUrl,
}) => {
  return (
    <div>
      <div className="rounded-lg border border-neutral-75 bg-neutral-30 p-6">
        {style === 'card' ? (
          <CardPreview
            primaryColor={primaryColor}
            providerName={providerName}
            squareLogoUrl={squareLogoUrl}
          />
        ) : (
          <InlinePreview primaryColor={primaryColor} />
        )}
      </div>
    </div>
  );
};

const CardPreview: FC<{
  primaryColor: string;
  providerName: string;
  squareLogoUrl?: string | null;
}> = ({ primaryColor, providerName, squareLogoUrl }) => {
  const initial = (providerName || 'P').charAt(0).toUpperCase();

  return (
    <div className="mx-auto max-w-[300px] rounded-xl border border-neutral-75 bg-white p-5 shadow-sm">
      <div className="mb-4 text-center">
        {squareLogoUrl ? (
          <img
            src={squareLogoUrl}
            alt="Logo"
            className="mx-auto mb-2 h-10 w-10 rounded-lg object-cover"
          />
        ) : (
          <div
            className="text-base mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {initial}
          </div>
        )}
        <div className="text-[15px] font-semibold text-neutral-150">
          Client Portal
        </div>
        <div className="mt-0.5 text-[13px] text-neutral-110">
          Access your resources and forms
        </div>
      </div>
      <div className="text-sm mb-3 rounded-lg border border-neutral-100 bg-white px-3 py-2.5 text-neutral-110">
        Enter your email address
      </div>
      <div
        className="text-sm rounded-lg px-4 py-2.5 text-center font-medium text-white"
        style={{ backgroundColor: primaryColor }}
      >
        Continue to Portal &rarr;
      </div>
      <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-neutral-110/50">
        Powered by{' '}
        <img src={hcLogo} alt="Homecoming" className="h-3 w-3 rounded-sm" />{' '}
        <span className="font-semibold">Homecoming</span>
      </div>
    </div>
  );
};

const InlinePreview: FC<{
  primaryColor: string;
}> = ({ primaryColor }) => (
  <div className="mx-auto max-w-[360px]">
    <div className="mb-1 text-[15px] font-semibold text-neutral-150">
      Access your Client Portal
    </div>
    <div className="mb-3 text-[13px] text-neutral-110">
      Enter your email to log in and view your resources and forms.
    </div>
    <div className="flex gap-2">
      <div className="text-sm flex-1 rounded-lg border border-neutral-100 bg-white px-3 py-2.5 text-neutral-110">
        Email address
      </div>
      <div
        className="text-sm whitespace-nowrap rounded-lg px-4 py-2.5 font-medium text-white"
        style={{ backgroundColor: primaryColor }}
      >
        Log in &rarr;
      </div>
    </div>
    <div className="mt-1.5 flex items-center justify-end gap-1 text-[11px] text-neutral-110/50">
      Powered by{' '}
      <img src={hcLogo} alt="Homecoming" className="h-3 w-3 rounded-sm" />{' '}
      <span className="font-semibold">Homecoming</span>
    </div>
  </div>
);

export default WidgetPreview;
